import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
    addToast('You have been logged out.', 'info');
    navigate('/');
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-inner">
          <Link to={user ? '/dashboard' : '/'} className="navbar-brand">
            <img src="/rivitlogo.svg" alt="Rivit" className="navbar-logo-img" />
          </Link>
          {user && (
            <div className="navbar-links">
              <Link to="/dashboard" className="nav-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                Dashboard
              </Link>
              {isAdmin && (
                <Link to="/admin" className="nav-link nav-link-admin">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  Admin Panel
                </Link>
              )}
              <button className="nav-link nav-link-ghost" onClick={() => setShowHowItWorks(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                How It Works
              </button>
              <button className="nav-link nav-link-ghost" onClick={() => setShowHelp(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Help
              </button>
              <div className="nav-user-section">
                <div className="nav-user-avatar">{user.fullName.charAt(0).toUpperCase()}</div>
                <span className="nav-user-name">{user.fullName}</span>
                <button className="btn btn-outline btn-sm" onClick={() => setShowLogoutConfirm(true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Logout Confirmation */}
      {showLogoutConfirm && (
        <div className="dialog-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </div>
            <h3 className="dialog-title">Sign Out</h3>
            <p className="dialog-message">Are you sure you want to log out of your account?</p>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleLogout}>Logout</button>
            </div>
          </div>
        </div>
      )}

      {/* How It Works Dialog */}
      {showHowItWorks && (
        <div className="dialog-overlay" onClick={() => setShowHowItWorks(false)}>
          <div className="dialog dialog-lg" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3 className="dialog-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.5rem', verticalAlign: 'middle'}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                How It Works
              </h3>
              <button className="dialog-close-btn" onClick={() => setShowHowItWorks(false)}>&times;</button>
            </div>
            <div className="dialog-body">
              <div className="how-steps">
                <div className="how-step">
                  <div className="how-step-num">1</div>
                  <div className="how-step-content">
                    <h4>Export Your DXL</h4>
                    <p>From your Lotus Notes/Domino application, export the design as DXL (Domino XML Language). You can export individual databases or bundle multiple DXL files into a single ZIP archive.</p>
                  </div>
                </div>
                <div className="how-step">
                  <div className="how-step-num">2</div>
                  <div className="how-step-content">
                    <h4>Submit for Analysis</h4>
                    <p>Use our <strong>Single App</strong> form for individual applications, or the <strong>Mass Import</strong> form to submit an entire portfolio of Notes applications at once. Fill in the details about your app and upload the file.</p>
                  </div>
                </div>
                <div className="how-step">
                  <div className="how-step-num">3</div>
                  <div className="how-step-content">
                    <h4>We Analyze</h4>
                    <p>Our team reviews the DXL structure — forms, views, agents, security ACLs, integrations, and workflows. We assess complexity and identify migration challenges.</p>
                  </div>
                </div>
                <div className="how-step">
                  <div className="how-step-num">4</div>
                  <div className="how-step-content">
                    <h4>Receive Your Report</h4>
                    <p>Once analysis is complete, a detailed migration readiness report will appear on your dashboard. It includes effort estimates, risk areas, and recommended target platform.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="dialog-actions">
              <button className="btn btn-primary" onClick={() => setShowHowItWorks(false)}>Got It</button>
            </div>
          </div>
        </div>
      )}

      {/* Help / Terms Dialog */}
      {showHelp && (
        <div className="dialog-overlay" onClick={() => setShowHelp(false)}>
          <div className="dialog dialog-lg" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3 className="dialog-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.5rem', verticalAlign: 'middle'}}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Help &amp; Data Handling
              </h3>
              <button className="dialog-close-btn" onClick={() => setShowHelp(false)}>&times;</button>
            </div>
            <div className="dialog-body">
              <div className="help-section">
                <h4>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  How We Handle Your DXL Files
                </h4>
                <p>Your DXL files are stored securely on our encrypted servers. They are used solely for migration analysis and are never shared with third parties. Files are retained for the duration of your engagement and deleted upon request.</p>
              </div>
              <div className="help-section">
                <h4>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  What is a DXL File?
                </h4>
                <p>DXL (Domino XML Language) is the standard XML export format for Lotus Notes/Domino applications. It contains the design elements — forms, views, agents, ACLs — that we analyze to plan your migration.</p>
              </div>
              <div className="help-section">
                <h4>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                  Single App vs Mass Import
                </h4>
                <p><strong>Single App:</strong> For one Lotus Notes application at a time. Provide detailed info and upload one DXL/XML file.</p>
                <p><strong>Mass Import:</strong> For an entire portfolio. Upload a ZIP file containing multiple DXL exports. Answer a few high-level questions about shared infrastructure.</p>
              </div>
              <div className="help-section">
                <h4>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                  Terms of Use
                </h4>
                <p>
                  By uploading files, you confirm you have authorization to share these application designs. All analysis is confidential. Reports are provided for planning purposes and do not constitute a binding migration commitment. Read our{' '}
                  <a href="https://www.rivit.ca/privacy-policy/" target="_blank" rel="noreferrer">Terms and Conditions</a>.
                </p>
              </div>
              <div className="help-section">
                <h4>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  Contact Info
                </h4>
                <div className="help-contact-list">
                  <div className="help-contact-row">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.86 19.86 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.79.61 2.64a2 2 0 0 1-.45 2.11L8 9.91a16 16 0 0 0 6 6l1.44-1.27a2 2 0 0 1 2.11-.45c.85.28 1.74.49 2.64.61A2 2 0 0 1 22 16.92z"/></svg>
                    <a href="tel:+16479301711">647.930.1711</a>
                  </div>
                  <div className="help-contact-row">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v5H2v-5a6 6 0 0 1 6-6"/><circle cx="12" cy="4" r="2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                    <a href="mailto:info@rivit.ca">info@rivit.ca</a>
                  </div>
                  <div className="help-contact-row">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 4 4v4a4 4 0 0 1-4 4h-1"/><path d="M6 8H5a4 4 0 0 0-4 4v4a4 4 0 0 0 4 4h1"/><rect x="6" y="4" width="12" height="16" rx="2"/><line x1="10" y1="14" x2="14" y2="14"/></svg>
                    <a href="mailto:support@rivit.ca">support@rivit.ca</a>
                  </div>
                  <div className="help-contact-row">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    <span>Toronto, Canada</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="dialog-actions">
              <button className="btn btn-primary" onClick={() => setShowHelp(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
